import { useMemo, useState, type ReactNode } from 'react';
import {
  buildSectionTree,
  DOCUMENT_LIMITS,
  type DocumentSection,
  type SectionTreeNode,
} from '@oscom/shared';
import type { MoveAction } from '../documents/sectionTree';

/** Chapter tree of the editor with numbers from the render result. */
export function SectionTree({
  sections,
  numbers,
  selectedId,
  requiredIds,
  canWrite,
  busy,
  onSelect,
  onAdd,
  onMove,
  onDelete,
}: {
  sections: readonly DocumentSection[];
  numbers: ReadonlyMap<string, string>;
  selectedId: string | undefined;
  requiredIds: ReadonlySet<string>;
  canWrite: boolean;
  busy: boolean;
  onSelect: (sectionId: string) => void;
  onAdd: (parentSectionId: string | undefined) => void;
  onMove: (sectionId: string, action: MoveAction) => void;
  onDelete: (sectionId: string) => void;
}): ReactNode {
  const tree = useMemo(() => buildSectionTree(sections), [sections]);
  const [confirming, setConfirming] = useState<string | undefined>(undefined);
  const maxDepth = DOCUMENT_LIMITS.maxSectionDepth;

  const renderNodes = (
    nodes: readonly SectionTreeNode<DocumentSection>[],
    level: number,
  ): ReactNode => (
    <ul className="section-tree__list">
      {nodes.map((node) => {
        const section = node.section;
        const label = `${numbers.get(section.id) ?? ''} ${section.title}`.trim();
        const name = `„${section.title}“`;
        return (
          <li key={section.id}>
            <div
              className={
                section.id === selectedId ? 'section-tree__row is-selected' : 'section-tree__row'
              }
            >
              <button
                type="button"
                className="section-tree__title"
                aria-current={section.id === selectedId ? 'true' : undefined}
                onClick={() => onSelect(section.id)}
              >
                {label}
              </button>
              {canWrite ? (
                <span className="section-tree__actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`${name} nach oben`}
                    disabled={busy}
                    onClick={() => onMove(section.id, 'up')}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`${name} nach unten`}
                    disabled={busy}
                    onClick={() => onMove(section.id, 'down')}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`${name} einrücken`}
                    disabled={busy || Boolean(section.generator) || level >= maxDepth}
                    onClick={() => onMove(section.id, 'indent')}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`${name} ausrücken`}
                    disabled={busy || level === 1}
                    onClick={() => onMove(section.id, 'outdent')}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`Unterkapitel zu ${name} hinzufügen`}
                    disabled={busy || level >= maxDepth}
                    onClick={() => onAdd(section.id)}
                  >
                    +
                  </button>
                  {requiredIds.has(section.id) ? null : confirming === section.id ? (
                    <>
                      <button
                        type="button"
                        className="button"
                        onClick={() => {
                          setConfirming(undefined);
                          onDelete(section.id);
                        }}
                      >
                        Löschen bestätigen
                      </button>
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={() => setConfirming(undefined)}
                      >
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="button button--ghost"
                      aria-label={`${name} löschen`}
                      disabled={busy}
                      onClick={() => setConfirming(section.id)}
                    >
                      Löschen
                    </button>
                  )}
                </span>
              ) : null}
            </div>
            {node.children.length > 0 ? renderNodes(node.children, level + 1) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav className="section-tree" aria-label="Kapitel">
      {renderNodes(tree, 1)}
      {canWrite ? (
        <button type="button" className="button" disabled={busy} onClick={() => onAdd(undefined)}>
          + Kapitel
        </button>
      ) : null}
    </nav>
  );
}
